<ng-container rtBlock="sc-constructor-page">
    <!-- The title row of the frame: the constructor bar on its right, above all three columns -->
    <div rtElem="head">
        <h1 rtElem="page-title" [attr.title]="pageTitle()">{{ pageTitle() }}</h1>
        <div rtElem="bar">
            <button
                rtElem="bar-btn"
                rtButton
                type="button"
                qa-dataid="constructor-load-file"
                [rtMod]="{ load: true }"
                (click)="barFileInput.click()">
                <svg
                    rtElem="bar-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true">
                    <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />
                </svg>
                <span i18n="@@btnLoadFile">Загрузить файл</span>
            </button>
            <!-- native-ok: the kit file input draws its own outlined button with a file card list, while the bar keeps one flat button and reads the file at once (owner decision: keep the site look) -->
            <input
                #barFileInput
                type="file"
                accept=".json"
                hidden
                qa-dataid="constructor-load-file-input"
                (change)="onBarFileSelected($event)" />
            <button rtElem="bar-btn" rtButton type="button" qa-dataid="constructor-share" [disabled]="justCopied()" (click)="onShare()">
                <svg
                    rtElem="bar-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true">
                    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
                </svg>
                <span i18n="@@btnShare">Поделиться</span>
            </button>
            <button rtElem="bar-btn" rtButton type="button" qa-dataid="constructor-save" [rtMod]="{ save: true }" (click)="onExport()">
                <svg
                    rtElem="bar-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true">
                    <path d="M12 4v12M7 11l5 5 5-5M4 20h16" />
                </svg>
                <span i18n="@@btnSaveCalc">Сохранить расчёт</span>
            </button>
        </div>
    </div>

    <aside rtElem="sidebar" qa-dataid="constructor-params-column">
        <sc-params-panel
            [config]="config()"
            [result]="result()"
            [showExtras]="!isWide()"
            [justCopied]="justCopied()"
            (configChange)="onConfigChange($event)"
            (configReset)="onConfigReset()"
            (exportRequested)="onExport()"
            (dxfRequested)="onExportDxf()"
            (partsRequested)="onExportParts()"
            (shareRequested)="onShare()"
            (paramChanged)="onParamChanged($event)"
            (feedbackOpened)="onFeedbackOpened()"
            (feedbackSubmitted)="onFeedbackSubmitted()"
            (feedbackFailed)="onFeedbackFailed($event)" />
    </aside>

    <section rtElem="viewport">
        <div rtElem="view-toggle">
            <button
                rtElem="view-btn"
                rtButton
                type="button"
                qa-dataid="constructor-view-3d"
                [rtMod]="{ active: viewMode() === ViewMode.ThreeD }"
                (click)="setViewMode(ViewMode.ThreeD)">
                3D
            </button>
            <button
                rtElem="view-btn"
                rtButton
                type="button"
                i18n="@@viewModePlan"
                qa-dataid="constructor-view-plan"
                [rtMod]="{ active: viewMode() === ViewMode.Plan }"
                (click)="setViewMode(ViewMode.Plan)">
                План
            </button>
            <button
                rtElem="view-btn"
                rtButton
                type="button"
                i18n="@@viewSide"
                qa-dataid="constructor-view-side"
                [rtMod]="{ active: viewMode() === ViewMode.Side }"
                (click)="setViewMode(ViewMode.Side)">
                Фасады
            </button>
        </div>

        <div rtElem="canvas-area">
            @defer (on idle) {
                <sc-viewport
                    qa-dataid="constructor-viewport-3d"
                    [config]="config()"
                    [result]="result()"
                    [class]="[viewMode() !== ViewMode.ThreeD && 'sc-constructor-page__view-hidden'] | concatClasses"
                    (openingDragChange)="onOpeningDrag($event)" />
                <sc-plan-view
                    #planView
                    qa-dataid="constructor-viewport-plan"
                    [config]="config()"
                    [result]="result()"
                    [sideOnly]="viewMode() === ViewMode.Side"
                    [class]="[viewMode() === ViewMode.ThreeD && 'sc-constructor-page__view-hidden'] | concatClasses" />
            } @placeholder {
                <div rtElem="viewport-placeholder" role="status" aria-busy="true">
                    <rt-spinner qa-dataid="constructor-viewport-loading" [diameter]="40" />
                </div>
            }

            @if (errorMessage()) {
                <div rtElem="error">
                    <span rtElem="error-icon">⚠</span>
                    <span>{{ errorMessage() }}</span>
                </div>
            }
        </div>

        @if (result()) {
            @if (viewMode() === ViewMode.ThreeD) {
                <div rtElem="viewport-hint" i18n="@@viewportHint">Зажмите ЛКМ для вращения · Колёсико для зума · ПКМ для перемещения</div>
            } @else {
                <div rtElem="viewport-hint" i18n="@@planViewHint">Колёсико для зума · ПКМ для перемещения · Двойной клик для сброса</div>
            }
        }
    </section>

    @if (isWide()) {
        <aside rtElem="results" qa-dataid="constructor-results-column">
            <sc-results-panel
                [config]="config()"
                [result]="result()"
                [justCopied]="justCopied()"
                (configChange)="onConfigChange($event)"
                (exportRequested)="onExport()"
                (dxfRequested)="onExportDxf()"
                (partsRequested)="onExportParts()"
                (shareRequested)="onShare()"
                (feedbackOpened)="onFeedbackOpened()"
                (feedbackSubmitted)="onFeedbackSubmitted()"
                (feedbackFailed)="onFeedbackFailed($event)" />
        </aside>
    }
</ng-container>
